import { z } from 'zod'
import { GameModeSchema, type GameMode } from './rules.js'
import type { GameEndReason } from './state.js'

/**
 * Single-elimination tournament. A player hosts it, others join with its code;
 * the host starts it, the bracket is drawn at random and every match loser is
 * out until one player is left. The code is also the tournament's identifier.
 */

export const TOURNAMENT_MIN_PLAYERS = 2
export const TOURNAMENT_MAX_PLAYERS = 32
export const TOURNAMENT_SIZES = [4, 8, 16, 32] as const
/** After this many drawn games in one match, a coin toss decides it. */
export const TOURNAMENT_MAX_DRAWS = 3
export const TOURNAMENT_NAME_MAX = 40

export const CreateTournamentSchema = z.object({
  name: z.string().trim().max(TOURNAMENT_NAME_MAX).optional(),
  maxPlayers: z.number().int().min(TOURNAMENT_MIN_PLAYERS).max(TOURNAMENT_MAX_PLAYERS).default(8),
  gameMode: GameModeSchema.default('capture'),
})
export type CreateTournamentInput = z.infer<typeof CreateTournamentSchema>

export const TournamentCodeSchema = z.object({ code: z.string().trim().min(4).max(32) })

export type TournamentStatus = 'registration' | 'running' | 'finished' | 'cancelled'

export interface TournamentPlayer {
  id: string
  name: string
}

/** How a match was settled (the game's end reason, or a coin toss after repeated draws). */
export type MatchEnd = GameEndReason | 'coin_toss'

export interface BracketMatch {
  /** 0 = first round. */
  round: number
  /** Position in the round, from the top of the bracket. */
  index: number
  /** Player ids; null while the previous match is not over. */
  players: [string | null, string | null]
  /** A lone player in the first round goes through without playing. */
  bye: boolean
  /** Current (or last) game of the match. */
  gameId: string | null
  /** Drawn games so far: the match is replayed, colors swapped. */
  draws: number
  winnerId: string | null
  endedBy: MatchEnd | null
}

export type MatchStatus = 'waiting' | 'playing' | 'done'

export function matchStatus(match: BracketMatch): MatchStatus {
  if (match.winnerId) return 'done'
  return match.gameId ? 'playing' : 'waiting'
}

export interface Tournament {
  /** Join code, also the identifier. */
  code: string
  name: string
  hostId: string
  hostName: string
  gameMode: GameMode
  maxPlayers: number
  status: TournamentStatus
  /** In order of registration. */
  players: TournamentPlayer[]
  /** Empty until the tournament starts. */
  rounds: BracketMatch[][]
  winnerId: string | null
  createdAt: string
}

/** A match is ready for the player: sent to them wherever they are on the site. */
export interface TournamentNotice {
  code: string
  name: string
  gameId: string
  round: number
  rounds: number
  opponentName: string
}

/** Reference kept on a tournament game. */
export interface TournamentRef {
  code: string
  round: number
  index: number
}

/** Size of the bracket (next power of two) for a number of players. */
export function bracketSize(players: number): number {
  let size = 2
  while (size < players) size *= 2
  return size
}
